import {
  Component,
  ElementRef,
  OnDestroy,
  computed,
  effect,
  forwardRef,
  inject,
  input,
  signal,
  untracked,
  viewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import {
  compatibilityContextVocabulary,
  compatibilityTargets,
  compatibilityVocabulary
} from '@core/filters/path-filter/vocabulary/compatibility.vocabulary';
import {
  CompatTarget,
  CompatibilityCfg,
  CompatibilityRequirement
} from '@core/filters/path-filter/wire';
import { JsonSchema } from '@core/models/transformers.model';
import { CurrentLangService } from '@core/services/common/current-lang.service';
import { EntityDisplayNamesService } from '@core/services/entity-display-names/entity-display-names.service';
import { ICONS } from '@core/services/icons/icons.service';

import { DropdownComponent } from '@shared/components/dropdown/dropdown.component';
import { DslTextComponent } from '@shared/components/path-filter/dsl-text/dsl-text.component';
import { PhraseSegment } from '@shared/components/path-filter/phrase/describe-filter';
import { PathFilterPhraseComponent } from '@shared/components/path-filter/phrase/path-filter-phrase.component';
import { FocusFirstDirective } from '@shared/directives/focus-first.directive';
import { AvailableLanguages } from '@shared/types/translation.type';

import { targetDisplayName, targetIcon } from './compatibility-display';
import { CompatibilityEditSessionService } from './compatibility-edit-session.service';
import { compatibilityFieldErrors } from './compatibility-errors';
import { decodeRequirement, summarizeRequirement } from './requirement-draft';
import { describeRequirement } from './requirement-phrase';

@Component({
  selector: 'app-compatibility-cfg-field',
  imports: [
    TranslatePipe,
    DropdownComponent,
    DslTextComponent,
    FocusFirstDirective,
    PathFilterPhraseComponent
  ],
  templateUrl: './compatibility-cfg-field.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CompatibilityCfgFieldComponent),
      multi: true
    }
  ]
})
export class CompatibilityCfgFieldComponent implements ControlValueAccessor, OnDestroy {
  protected readonly ICONS = ICONS;
  private readonly session = inject(CompatibilityEditSessionService);
  private readonly displayNames = inject(EntityDisplayNamesService);
  private readonly translate = inject(TranslateService);
  private readonly currentLang = inject(CurrentLangService).currentLang;
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly addTrigger = viewChild<ElementRef<HTMLElement>>('addTrigger');

  carrierSchema = input.required<JsonSchema>();
  entityType = input<string>('');

  protected readonly value = signal<CompatibilityCfg | null>(null);
  protected readonly isDisabled = signal(false);
  protected readonly addOpen = signal(false);
  /** The card whose requirement is open in the panel; null while adding one. */
  private readonly editingIndex = signal<number | null>(null);

  protected readonly targets = computed(() => compatibilityTargets(this.carrierSchema()));
  private readonly names = computed(() => this.displayNames.singularDisplayNames());
  private readonly selfLabel = computed(
    () => this.names().get(this.entityType().toUpperCase()) ?? this.entityType()
  );
  private readonly requirements = computed(() => this.value()?.all_of ?? []);
  protected readonly fieldErrors = computed(() =>
    compatibilityFieldErrors(this.session.saveError())
  );

  protected readonly cards = computed(() => {
    const offered = new Set<string>(this.targets());
    const editing = this.session.isActive() ? this.editingIndex() : null;
    return this.requirements().map((requirement, index) => ({
      requirement,
      label: targetDisplayName(requirement.target_entity_type, this.names()),
      icon: targetIcon(requirement.target_entity_type),
      penalty: requirement.violation_penalty ?? null,
      summary: summarizeRequirement(requirement),
      phrase: offered.has(requirement.target_entity_type)
        ? this.phraseOf(requirement)
        : null,
      editable: offered.has(requirement.target_entity_type),
      editing: editing === index
    }));
  });

  protected readonly addOptions = computed(() =>
    this.targets().map((target) => ({
      target,
      label: targetDisplayName(target, this.names()),
      icon: targetIcon(target)
    }))
  );

  /**
   * The requirement as the panel describes it. A stored requirement this build cannot decode
   * shows its DSL lines instead: a summary must not take the entity form down.
   */
  private phraseOf(requirement: CompatibilityRequirement): PhraseSegment[] | null {
    const carrier = this.carrierSchema();
    try {
      return describeRequirement(
        decodeRequirement(requirement, carrier),
        {
          filter: compatibilityVocabulary(carrier, requirement.target_entity_type),
          context: compatibilityContextVocabulary(carrier)
        },
        this.currentLang() as AvailableLanguages,
        this.translate,
        this.selfLabel()
      );
    } catch {
      return null;
    }
  }

  private onChange: (value: CompatibilityCfg | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    let wasActive = false;
    effect(() => {
      const active = this.session.isActive();
      if (wasActive && !active) {
        untracked(() => this.returnFocus());
      }
      wasActive = active;
    });
  }

  ngOnDestroy(): void {
    this.session.end();
  }

  writeValue(value: CompatibilityCfg | null): void {
    this.value.set(value ?? null);
  }

  registerOnChange(fn: (value: CompatibilityCfg | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  protected readonly closeAdd = (): void => this.addOpen.set(false);

  protected onAddMenuKey(event: KeyboardEvent): void {
    // The overlay stops key events at its panels, so its own Escape listener never sees this one.
    if (event.key !== 'Escape') return;
    this.closeAdd();
    this.addTrigger()?.nativeElement.focus();
  }

  protected edit(index: number): void {
    const requirement = this.requirements()[index];
    this.editingIndex.set(index);
    this.begin(requirement.target_entity_type, requirement, (applied) =>
      this.write(this.requirements().map((current, i) => (i === index ? applied : current)))
    );
  }

  protected add(target: CompatTarget): void {
    this.addOpen.set(false);
    this.editingIndex.set(null);
    this.begin(target, null, (applied) => this.write([...this.requirements(), applied]));
  }

  protected remove(index: number): void {
    // The indices after this one shift, so a panel left open would apply to the wrong card.
    this.session.end();
    this.editingIndex.set(null);
    this.write(this.requirements().filter((_, i) => i !== index));
  }

  /** The panel that held focus is gone: go back to the control that opened it. */
  private returnFocus(): void {
    const index = this.editingIndex();
    const editButton =
      index === null
        ? null
        : this.host.nativeElement.querySelector<HTMLElement>(
            `[data-edit-index="${index}"]`
          );
    (editButton ?? this.addTrigger()?.nativeElement)?.focus();
  }

  private begin(
    target: CompatTarget,
    requirement: CompatibilityRequirement | null,
    onApply: (requirement: CompatibilityRequirement) => void
  ): void {
    this.onTouched();
    this.session.begin({
      carrier: this.carrierSchema(),
      selfLabel: this.selfLabel(),
      target,
      requirement,
      onApply
    });
  }

  /**
   * Rebuilt from the stored requirement objects themselves, never from re-encoded drafts, so a
   * requirement the user did not apply in the panel is written back byte-identical. Keys other than
   * `all_of` are kept the same way, so the configuration only goes once nothing is left in it.
   */
  private write(requirements: CompatibilityRequirement[]): void {
    const { all_of: _, ...rest } = this.value() ?? {};
    const next: CompatibilityCfg | null =
      requirements.length > 0 || Object.keys(rest).length > 0
        ? { ...rest, all_of: requirements }
        : null;
    this.value.set(next);
    // A refused save was about the requirements as they were; once they change it no longer applies.
    this.session.setSaveError(null);
    // The row form only reports values once it has been touched.
    this.onTouched();
    this.onChange(next);
  }
}
